import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCountries } from '@/contexts/country';
import { Users, GamepadIcon, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { GameDetails } from '@/types/team';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from 'react-i18next';
import { DynamicRegistrationField } from '@/components/teamRegistration/DynamicRegistrationField';
import { validateFieldValue } from '@/utils/teamRegistration/validation';
import {
  buildRegistrationPayload,
  getActiveFields,
  getFieldMandatoryStatus,
} from '@/utils/teamRegistration/fieldMapping';
import { buildTeamMemberDetailsEditFormState } from './teamMemberDetailsEditForm.utils';
import type { TeamRegistrationField } from '@/types/tournament/game';
import type { PhoneData } from '@/types/tournament/quickRegistration';
import { RegistrationContext } from '@/types/tournament/game';
import { TeamInviteModalMode, TeamMember, TeamMemberRole } from '@/types/team';

interface TeamInviteAcceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (formData: TeamInviteFormData) => void;
  teamData?: { name?: string; gameDetails?: GameDetails | null } | null;
  isLoading?: boolean;
  isLoadingTeamData?: boolean;
  inviteError?: string | null;
  membershipResult?: { role: string; found: boolean } | null;
  role?: TeamMemberRole | string;
  context?: RegistrationContext;
  /** When `TeamInviteModalMode.EDIT`, form is prefilled from `memberToEdit` and copy targets updating details. */
  mode?: TeamInviteModalMode;
  memberToEdit?: TeamMember | null;
}

export type TeamInviteFormData = Record<string, unknown>;

const TeamInviteAcceptModal = ({
  isOpen,
  onClose,
  onAccept,
  teamData,
  isLoading = false,
  isLoadingTeamData = false,
  inviteError = null,
  membershipResult,
  role,
  context,
  mode = TeamInviteModalMode.JOIN,
  memberToEdit = null,
}: TeamInviteAcceptModalProps) => {
  const { user } = useAuth();
  const { t } = useTranslation('team');
  const isEditMode = mode === TeamInviteModalMode.EDIT;

  // Use global country context
  const {
    countryCodes,
    getCountryByCode,
    isLoading: isCountriesLoading,
  } = useCountries();

  // Filter fields based on roleValidationRules if role and context are provided
  const registrationFields = useMemo(() => {
    const allFields = (teamData?.gameDetails?.teamRegistrationFields ||
      []) as TeamRegistrationField[];

    // If no role or context provided, return empty array (should not happen, but safe fallback)
    if (!role || !context) {
      return [];
    }

    // Normalize role to TeamMemberRole enum value
    const normalizedRole =
      typeof role === 'string' ? (role.toLowerCase() as TeamMemberRole) : role;

    return getActiveFields(allFields, normalizedRole, context);
  }, [teamData?.gameDetails?.teamRegistrationFields, role, context]);

  const [formData, setFormData] = useState<Record<string, string | PhoneData>>(
    {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initializeFormDataRef = useRef<
    () => Record<string, string | PhoneData>
  >(() => ({}));

  initializeFormDataRef.current = () => {
    const data: Record<string, string | PhoneData> = {};

    for (const field of registrationFields) {
      if (field.fieldKey === 'mobileNo') {
        const fallbackCountry = user?.countryCode?.code
          ? getCountryByCode(
              user.countryCode.code,
              user.countryCode.abbreviation,
            ) || countryCodes[0]
          : countryCodes[0];
        data[field.fieldKey] = {
          code: fallbackCountry?.code || '',
          number: '',
          countryName: fallbackCountry?.name || null,
          abbreviation: fallbackCountry?.abbreviation || null,
        };
      } else if (field.fieldKey === 'email') {
        data[field.fieldKey] = user?.email || '';
      } else {
        data[field.fieldKey] = '';
      }
    }

    return data;
  };

  useEffect(() => {
    if (!isOpen || registrationFields.length === 0) return;

    setErrors({});
    const defaults = initializeFormDataRef.current();

    if (isEditMode && memberToEdit) {
      setFormData(
        buildTeamMemberDetailsEditFormState(
          defaults,
          memberToEdit,
          registrationFields,
        ),
      );
    } else {
      setFormData(defaults);
    }
    // Defaults built via ref (updated each render) so the effect deps stay narrow; user/country
    // churn does not reset the form mid-edit.
  }, [isOpen, registrationFields, isEditMode, memberToEdit]);

  const handleFieldChange = (fieldKey: string, value: string | PhoneData) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    if (errors[fieldKey]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    }
  };

  const handleFieldBlur = (field: TeamRegistrationField) => {
    const value = formData[field.fieldKey];
    // Get dynamic mandatory from roleValidationRules
    const normalizedRole =
      typeof role === 'string'
        ? (role.toLowerCase() as TeamMemberRole)
        : role || TeamMemberRole.PLAYERS;
    const isMandatory = getFieldMandatoryStatus(field, normalizedRole, context);

    const validation = validateFieldValue(field, value, isMandatory);

    if (!validation.isValid && validation.error) {
      setErrors(prev => ({ ...prev, [field.fieldKey]: validation.error }));
    } else if (errors[field.fieldKey]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field.fieldKey];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const normalizedRole =
      typeof role === 'string'
        ? (role.toLowerCase() as TeamMemberRole)
        : role || TeamMemberRole.PLAYERS;

    for (const field of registrationFields) {
      const value = formData[field.fieldKey];
      // Get dynamic mandatory from roleValidationRules
      const isMandatory = getFieldMandatoryStatus(
        field,
        normalizedRole,
        context,
      );

      const validation = validateFieldValue(field, value, isMandatory);

      if (!validation.isValid && validation.error) {
        newErrors[field.fieldKey] = validation.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Join + edit: show registration form / “fields not configured” when not blocked by invite membership UI. */
  const showTeamRegistrationFormSection =
    isOpen && !isLoadingTeamData && (!membershipResult?.found || isEditMode);

  const handleSubmit = () => {
    if (!isEditMode && inviteError) return;
    if (!validateForm()) return;

    const payload = buildRegistrationPayload(
      formData,
      registrationFields,
    ) as TeamInviteFormData;

    onAccept(payload);
  };

  // Show loading state while countries are being fetched
  if (isCountriesLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className='w-full max-w-[95vw] sm:max-w-md bg-gray-900 border-purple-500/20 text-white px-4 sm:px-6'>
          <DialogHeader>
            <DialogTitle className='text-white flex items-center flex-wrap'>
              <Users className='w-5 h-5 me-2 text-purple-400' />
              <span>
                {isEditMode
                  ? t('inviteAccept.editDetailsTitle', 'Edit your details')
                  : t('inviteAccept.joinTeam')}{' '}
              </span>
              {!isEditMode && teamData?.name && (
                <span className='md:ms-1 block md:inline break-words break-all md:break-normal'>
                  {teamData.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className='flex justify-center items-center h-32'>
            <div className='text-white'>
              {t('inviteAccept.loadingCountries', 'Loading countries...')}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='w-full max-w-[95vw] sm:max-w-md bg-gray-900 border-purple-500/20 text-white px-4 sm:px-6'>
        <DialogHeader>
          <DialogTitle className='text-white flex items-center flex-wrap'>
            <Users className='w-5 h-5 me-2 text-purple-400' />
            {isLoadingTeamData ? (
              <span className='flex items-center'>
                <Loader2 className='w-4 h-4 me-2 animate-spin' />
                {t(
                  'inviteAccept.loadingTeamDetails',
                  'Loading team details...',
                )}
              </span>
            ) : (
              <>
                <span>
                  {isEditMode
                    ? t('inviteAccept.editDetailsTitle', 'Edit your details')
                    : t('inviteAccept.joinTeam', 'Join Team :')}{' '}
                </span>
                {!isEditMode && teamData?.name && (
                  <span className='md:ms-1 block md:inline break-words break-all md:break-normal'>
                    {teamData.name}
                  </span>
                )}
              </>
            )}
          </DialogTitle>
          {isLoadingTeamData ? (
            <div className='flex items-center text-sm text-gray-400 mt-2'>
              <Loader2 className='w-3 h-3 me-2 animate-spin' />
              {t('inviteAccept.validatingInvite', 'Validating invite link...')}
            </div>
          ) : teamData?.gameDetails ? (
            <div className='flex items-center text-sm text-gray-400 mt-2'>
              <GamepadIcon className='w-4 h-4 me-2' />
              {teamData.gameDetails.name}
            </div>
          ) : null}
        </DialogHeader>
        {inviteError && (
          <Alert className='bg-red-900/20 border-red-500/20 mb-4'>
            <AlertCircle className='h-4 w-4 text-red-400' />
            <AlertDescription className='text-red-300'>
              {inviteError}
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State - Centered spinner */}
        {isLoadingTeamData && (
          <div className='flex flex-col items-center justify-center py-12 space-y-4'>
            <Loader2 className='w-8 h-8 animate-spin text-purple-400' />
            <p className='text-gray-400 text-sm'>
              {t(
                'inviteAccept.checkingMembership',
                'Checking team membership...',
              )}
            </p>
          </div>
        )}

        {/* Membership Message - User is already in team */}
        {!isLoadingTeamData && !isEditMode && membershipResult?.found && (
          <div className='flex flex-col items-center justify-center py-8 space-y-4'>
            <div className='bg-blue-900/20 border border-blue-500/20 rounded-lg p-6 text-center'>
              <Users className='w-12 h-12 mx-auto text-blue-400 mb-3' />
              <h3 className='text-lg font-semibold text-white mb-2'>
                {t('inviteAccept.alreadyMember', 'Already a Team Member')}
              </h3>
              <p className='text-gray-300'>
                {t(
                  'inviteAccept.alreadyMemberDesc',
                  'You are already a member of this team with role:',
                )}{' '}
                <span className='font-semibold text-blue-400'>
                  {membershipResult.role}
                </span>
              </p>
            </div>
            <Button
              onClick={() => {
                onClose();
              }}
              variant='outline'
              className='mt-4'
            >
              {t('inviteAccept.close', 'Close')}
            </Button>
          </div>
        )}

        {/* Content when registration fields are not configured */}
        {showTeamRegistrationFormSection && registrationFields.length === 0 && (
          <div className='flex flex-col items-center justify-center py-8 space-y-4'>
            <Alert className='bg-yellow-900/20 border-yellow-500/20 max-w-md text-center'>
              <AlertCircle className='h-4 w-4 text-yellow-400' />
              <AlertDescription className='text-yellow-200'>
                {t('inviteAccept.fieldsNotConfigured')}
              </AlertDescription>
            </Alert>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              className='mt-2'
              disabled={isLoading}
            >
              {t('inviteAccept.close', 'Close')}
            </Button>
          </div>
        )}

        {/* Form - Only show when modal is open, not loading, user is not a member, and fields are configured */}
        {showTeamRegistrationFormSection && registrationFields.length > 0 && (
          <form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
            className='space-y-4'
          >
            {registrationFields.map(field => {
              // Get dynamic mandatory from roleValidationRules
              const normalizedRole =
                typeof role === 'string'
                  ? (role.toLowerCase() as TeamMemberRole)
                  : role || TeamMemberRole.PLAYERS;
              const isMandatory = getFieldMandatoryStatus(
                field,
                normalizedRole,
                context,
              );

              return (
                <DynamicRegistrationField
                  key={field.fieldKey}
                  field={field}
                  value={
                    formData[field.fieldKey] ||
                    (field.fieldKey === 'mobileNo'
                      ? {
                          code:
                            user?.countryCode?.code ||
                            countryCodes[0]?.code ||
                            '',
                          number: '',
                          countryName:
                            user?.countryCode?.name ||
                            countryCodes[0]?.name ||
                            null,
                          abbreviation:
                            user?.countryCode?.abbreviation ||
                            countryCodes[0]?.abbreviation ||
                            null,
                        }
                      : '')
                  }
                  onChange={value => handleFieldChange(field.fieldKey, value)}
                  onBlur={() => handleFieldBlur(field)}
                  error={errors[field.fieldKey]}
                  isEmailReadOnly={field.fieldKey === 'email'}
                  defaultEmail={user?.email || ''}
                  isMandatory={isMandatory}
                />
              );
            })}

            <div className='flex gap-3 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={onClose}
                className='flex-1'
                disabled={isLoading}
              >
                {t('inviteAccept.cancel', 'Cancel')}
              </Button>
              <Button
                type='submit'
                className='flex-1 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
                disabled={
                  isLoading ||
                  (!!inviteError && !isEditMode) ||
                  isLoadingTeamData
                }
              >
                {isLoading
                  ? isEditMode
                    ? t('inviteAccept.saving', 'Saving...')
                    : t('inviteAccept.joining', 'Joining...')
                  : isEditMode
                    ? t('inviteAccept.saveDetails', 'Save')
                    : t('inviteAccept.acceptInvite', 'Accept Invite')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeamInviteAcceptModal;
